import React from 'react'

import { Box, Skeleton as CSkeleton, Stack } from '@mui/material'

import { theme } from 'styles/theme'

import { Separator } from '../Item/styles'

type BarProps = {
  width: number | string
  height?: number
  light?: boolean
}

/** Barra de texto simples, reaproveitada nas linhas e tabelas do skeleton. */
const Bar = ({ width, height = 16, light = false }: BarProps) => (
  <CSkeleton
    variant="text"
    width={width}
    height={height}
    animation="wave"
    sx={{
      bgcolor: light ? theme.palette.neutral[700] : theme.palette.neutral[600]
    }}
  />
)

/** Mimetiza um <Item /> (label + separador tracejado + valor). */
const InfoRowSkeleton = ({
  labelWidth,
  valueWidth
}: {
  labelWidth: number
  valueWidth: number
}) => (
  <Box display="flex" gap={1.25} alignItems="center" margin="10px 0">
    <Bar width={labelWidth} height={16} light />
    <Separator sx={{ marginTop: '2px' }} />
    <Bar width={valueWidth} height={16} />
  </Box>
)

/** Cabeçalho do accordion: quadrado do ícone (ContainerIcon) + título. */
const AccordionHeaderSkeleton = () => (
  <Stack direction="row" alignItems="center" gap="8px" p="12px 16px">
    <CSkeleton
      variant="rounded"
      width={35}
      height={35}
      animation="wave"
      sx={{ bgcolor: theme.palette.neutral[700], borderRadius: '8px' }}
    />
    <Bar width={160} height={16} light />
  </Stack>
)

/** Tabela de 4 colunas (Rateio / Composição da despesa). */
const TableSkeleton = () => (
  <Box px="16px" pb="16px">
    <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} pb={1.5}>
      {[70, 70, 50, 30].map((width, index) => (
        <Bar key={index} width={width} height={14} light />
      ))}
    </Box>
    {[0, 1].map((row) => (
      <Box
        key={row}
        display="grid"
        gridTemplateColumns="repeat(4, 1fr)"
        gap={2}
        py={1}
      >
        {[120, 110, 80, 50].map((width, index) => (
          <Bar key={index} width={width} height={16} />
        ))}
      </Box>
    ))}
  </Box>
)

/** Accordion: borda esquerda verde + cabeçalho; opcionalmente expandido com tabela. */
const AccordionSkeleton = ({ withTable = false }: { withTable?: boolean }) => (
  <Box
    sx={{
      border: '1px solid #E8EBE8',
      borderLeft: '3px solid',
      borderLeftColor: 'primary.main',
      borderRadius: '8px',
      backgroundColor: '#fff'
    }}
  >
    <AccordionHeaderSkeleton />
    {withTable && (
      <>
        <Box sx={{ height: '1px', bgcolor: '#E8EBE8', mx: '16px' }} />
        <Box pt={1.5}>
          <TableSkeleton />
        </Box>
      </>
    )}
  </Box>
)

const Skeleton: React.FC = () => {
  return (
    <Box display="flex" flexDirection="column" width="100%" gap="10px">
      {/* Bloco superior: dados da despesa (espelha S.Session + <Item />) */}
      <Box
        sx={{
          marginTop: '12px',
          padding: '12px',
          border: '1px solid #EDEBEB',
          borderRadius: '5px'
        }}
      >
        <InfoRowSkeleton labelWidth={90} valueWidth={70} />
        <InfoRowSkeleton labelWidth={100} valueWidth={90} />
        <InfoRowSkeleton labelWidth={85} valueWidth={100} />
        <InfoRowSkeleton labelWidth={95} valueWidth={120} />
        <InfoRowSkeleton labelWidth={50} valueWidth={100} />
      </Box>

      {/* Accordions: dois expandidos com tabela + um recolhido */}
      <Stack mt="10px" gap="10px">
        <AccordionSkeleton withTable />
        <AccordionSkeleton withTable />
        <AccordionSkeleton />
      </Stack>
    </Box>
  )
}

export default Skeleton
